import React, { useEffect, useState } from 'react'
import { FullscreenExitOutlined, FullscreenOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'

interface FullscreenToggleProps {
  className?: string
  variant?: 'default' | 'toolbar'
}

const getFullscreenElement = () => {
  if (typeof document === 'undefined') {
    return null
  }

  return document.fullscreenElement
}

const isFullscreenSupported = () => {
  if (typeof document === 'undefined') {
    return false
  }

  return typeof document.documentElement.requestFullscreen === 'function'
}

const FullscreenToggle: React.FC<FullscreenToggleProps> = ({ className, variant = 'default' }) => {
  const { t } = useTranslation('common')
  const [isFullscreen, setIsFullscreen] = useState(false)
  const supported = isFullscreenSupported()

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined
    }

    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(getFullscreenElement()))
    }

    handleFullscreenChange()
    document.addEventListener('fullscreenchange', handleFullscreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  const handleToggle = async () => {
    if (!supported) {
      return
    }

    try {
      if (getFullscreenElement()) {
        await document.exitFullscreen()
        return
      }

      await document.documentElement.requestFullscreen()
    } catch (error) {
      console.error('Failed to toggle fullscreen mode', error)
    }
  }

  const label = isFullscreen ? t('header.exitFullscreen') : t('header.enterFullscreen')
  const Icon = isFullscreen ? FullscreenExitOutlined : FullscreenOutlined
  const buttonNode = variant === 'toolbar' ? (
    <button
      type="button"
      className={className}
      data-open={isFullscreen ? 'true' : 'false'}
      aria-label={label}
      onClick={() => {
        void handleToggle()
      }}
      disabled={!supported}
    >
      <Icon style={{ fontSize: 18 }} />
    </button>
  ) : (
    <button
      type="button"
      className={className}
      data-open={isFullscreen ? 'true' : 'false'}
      aria-label={label}
      onClick={() => {
        void handleToggle()
      }}
      disabled={!supported}
      style={className ? undefined : {
        cursor: supported ? 'pointer' : 'not-allowed',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        padding: 0,
        borderRadius: '999px',
        border: '1px solid var(--color-border)',
        background: isFullscreen ? 'color-mix(in srgb, var(--color-primary) 10%, var(--color-card) 90%)' : 'var(--color-card)',
        color: isFullscreen ? 'var(--color-primary)' : 'var(--color-text-secondary)',
        userSelect: 'none',
        lineHeight: 1,
        opacity: supported ? 1 : 0.45,
      }}
    >
      <Icon style={{ fontSize: 16 }} />
    </button>
  )

  return (
    <Tooltip title={label}>
      <span style={{ display: 'inline-flex' }}>
        {buttonNode}
      </span>
    </Tooltip>
  )
}

export default FullscreenToggle
